/**
 * Subscription-key derivation.
 *
 * A realtime subscription is identified by a stable string derived from a chart
 * group's `{symbol, frequency, indicatorRefs}`. The key is order-insensitive in
 * the indicator refs so that reordering the same indicator set reuses the same
 * subscription.
 */

import type { ChartGroupRequest } from "./request.js";

/** Opaque, comparable subscription identifier. */
export type SubscriptionKey = string;

/** The minimal shape needed to derive a {@link SubscriptionKey}. */
export interface SubscriptionInput {
  symbol: string;
  frequency: string;
  /** Stable indicator references (aliases or shortnames). */
  indicatorRefs: readonly string[];
}

/**
 * Derive a stable subscription key. Symbol is upper-cased (QV treats symbols
 * case-insensitively) and indicator refs are de-duplicated and sorted so the key
 * is independent of ordering.
 */
export function subscriptionKey(input: SubscriptionInput): SubscriptionKey {
  const symbol = input.symbol.trim().toUpperCase();
  const frequency = input.frequency.trim();
  const refs = [...new Set(input.indicatorRefs)].sort();
  return `${symbol}|${frequency}|${refs.join(",")}`;
}

/**
 * Derive the subscription key for a request chart group. Indicator refs are the
 * flattened, deduplicated output aliases (falling back to shortname when an
 * indicator declares no aliases).
 */
export function chartGroupSubscriptionKey(
  cg: ChartGroupRequest,
): SubscriptionKey {
  const refs: string[] = [];
  for (const ind of cg.indicators) {
    const aliases = ind.aliases ?? [];
    if (aliases.length > 0) refs.push(...aliases);
    else refs.push(ind.shortname);
  }
  return subscriptionKey({
    symbol: cg.symbol,
    frequency: cg.frequency,
    indicatorRefs: refs,
  });
}
